import { createContext, useContext, ReactNode, useState, useEffect } from "preact/compat";
import emitter from "@webcore/core/events/eventemitter";
import AppEvents from "../events/events";
import { route } from "preact-router";
import getUserDevice from "@/utils/getUserDevice";
import { getEffectiveCurrentPath } from "@/utils/getEffectiveCurrentPath";
import { __internal as AppContextStoreInternal } from "./AppContextStore";

export enum AppMode {
  TWIN = "twin",
  GROUND = "ground",
  IMAGE = "image",
  VPS = "vps"
}

export enum AppState {
  LOADING = "loading",
  SPLASH = "splash",
  ONBOARDING = "onboarding",
  TWIN_VIEWER = "twin_viewer",
  AR_SPLASH = "ar_splash",
  AR_VIEWER = "ar_viewer",
  AR_PLACING = "ar_placement",
  MODAL = "modal",
  LOCALIZING = "localizing",
  EXPERIENCE = "experience",
  RECORDING = "recording",
  PREVIEW = "preview",
  INFO = "info",
}

export enum RecordingState {
  NONE = "none",
  RECORDING = "recording",
  PREVIEW = "preview",
  SHARING = "sharing",
}

export type ModalType = "info" | "recording" | "preview";

interface AppContextType {
  mode: AppMode;
  appState: AppState;
  recordingState: RecordingState;
  modalType: ModalType | null;
  hasSeenOnboarding: boolean;
  hasSeenTwinInstructions: boolean;
  hasSeenInteractionInstructions: boolean;
  
  setMode: (mode: AppMode) => void;
  setAppState: (state: AppState) => void;
  setRecordingState: (state: RecordingState) => void;
  showModal: (type: ModalType) => void;
  hideModal: () => void;
  startAR: (shouldRoute?: boolean) => void;
  positionContent: () => void;
  xrReady: () => void;
  markOnboardingComplete: () => void;
  markTwinInstructionsComplete: () => void;
  markInteractionInstructionsComplete: () => void;
  
  isTwinMode: boolean;
  isGroundMode: boolean;
  isImageMode: boolean;
  isVPSMode: boolean;
  isTwinViewerState: boolean;
  isARViewerState: boolean;
  isModalState: boolean;
  isArActive: boolean;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within AppProvider");
  }
  return context;
};

interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider = ({ children }: AppProviderProps) => {
  const getInitialState = (): AppState => {
    return AppState.LOADING;
    // if (getEffectiveCurrentPath() === "/twin") {
    //   return AppState.TWIN_VIEWER;
    // }
    // if (!onboardingComplete) {
    //   return AppState.ONBOARDING;
    // }
    
    // if (currentMode === AppMode.GROUND || currentMode === AppMode.IMAGE || currentMode === AppMode.VPS) {
    //   return AppState.AR_SPLASH;
    // } else {
    //   return AppState.TWIN_VIEWER;
    // }
  };

  const getStateForMode = (newMode: AppMode, _onboardingComplete: boolean): AppState => {
    // if (!_onboardingComplete) {
    //   return AppState.ONBOARDING;
    // }
    
    if (newMode === AppMode.GROUND || newMode === AppMode.IMAGE || newMode === AppMode.VPS) {
      if(arActive){
        return _onboardingComplete ? AppState.EXPERIENCE : AppState.ONBOARDING;
      }
      else{
        return AppState.AR_SPLASH;
      }
    } else {
      return AppState.SPLASH;
    }
  };

  // If on /onsite-decal or /onsite-screen and NOT mobile, treat as Digital Twin (desktop AR shows Twin experience)
  const [mode, setMode] = useState<AppMode>(() => {
    const { isMobile: isMobileDevice } = getUserDevice();
    const effectivePathname = getEffectiveCurrentPath();
    
    let initialModeValue: AppMode;
    if(isMobileDevice){
      if(effectivePathname === "/ground"){
        initialModeValue = AppMode.GROUND;
      } else if(effectivePathname === "/image"){
        initialModeValue = AppMode.IMAGE;
      } else if(effectivePathname === "/vps"){
        initialModeValue = AppMode.VPS;
      } else {
        initialModeValue = AppMode.TWIN;
      }
    } else {
      initialModeValue = AppMode.TWIN;
    }
    
    // Initialize the module-level store with the initial mode (only runs once)
    AppContextStoreInternal.setCurrentMode(initialModeValue);
    
    return initialModeValue;
  });
  
  const [hasSeenOnboarding, setHasSeenOnboarding] = useState<boolean>(false);
  const [hasSeenTwinInstructions, setHasSeenTwinInstructions] = useState<boolean>(false);
  const [hasSeenInteractionInstructions, setHasSeenInteractionInstructions] = useState<boolean>(false);
  const [appState, setAppState] = useState<AppState>(() => {
    return getInitialState(); 
  });
  const [modalType, setModalType] = useState<ModalType | null>(null);
  const [recordingState, setRecordingState] = useState<RecordingState>(RecordingState.NONE);
  const [arActive, setArActive] = useState<boolean>(false);

  useEffect(() => {
    const handle8wKilled = () => {
      setArActive(false);
    };
    AppEvents.on("8w-killed", handle8wKilled);
    return () => AppEvents.off("8w-killed", handle8wKilled);
  }, []);

  useEffect(() => {
    const handle8wInitialized = () => {
      setArActive(true);
    };
    AppEvents.on("8w-initialized", handle8wInitialized);
    return () => AppEvents.off("8w-initialized", handle8wInitialized);
  }, []);

  useEffect(() => {
    const newState = getStateForMode(mode, hasSeenOnboarding);
    setAppState(newState);
  }, [mode, hasSeenOnboarding]);

  useEffect(() => {
    console.log('appState', appState);
  }, [appState]);

  useEffect(() => {
    console.log('appMode', mode);
    // Update module-level store for non-React code to query
    AppContextStoreInternal.setCurrentMode(mode);
    // Emit mode change event for logic controllers
    AppEvents.emit('app-mode-changed', mode);
  }, [mode]);

  useEffect(() => {
    const handlePathChange = () => {
      const effectivePath = getEffectiveCurrentPath();
      const { isMobile } = getUserDevice();
      
      let newMode: AppMode;
      if (effectivePath === "/ground" && isMobile) {
        newMode = AppMode.GROUND;
      } else if (effectivePath === "/image" && isMobile) {
        newMode = AppMode.IMAGE;
      } else if (effectivePath === "/vps" && isMobile) {
        newMode = AppMode.VPS;
      } else {
        newMode = AppMode.TWIN;
      }
      
      if (newMode !== mode) {
        setMode(newMode);
      }
    };
    window.addEventListener("popstate", handlePathChange);
    return () => window.removeEventListener("popstate", handlePathChange);
  }, [mode]);

  const showModal = (type: ModalType) => {
    setAppState(AppState.MODAL);
    setModalType(type);
  };

  const hideModal = () => {
    setModalType(null);
    // Return to previous state based on mode
    if (mode === AppMode.GROUND || mode === AppMode.IMAGE || mode === AppMode.VPS) {
      setAppState(AppState.AR_VIEWER);
    } else {
      setAppState(AppState.TWIN_VIEWER);
    }
  };

  const startAR = (shouldRoute: boolean = false) => {
    if (typeof window !== 'undefined' && (window as any).XR8) {
      xrReady();
      // Route to /ground only on mobile; desktop stays Twin
      const { isMobile } = getUserDevice();
      if (shouldRoute && mode === AppMode.TWIN && isMobile) route("/ground");
    } else {
      emitter.emit("xr-start");
      
      const { isMobile } = getUserDevice();
      if (shouldRoute && isMobile) {
        const handleXRStarted = () => {
          emitter.off("xr-started", handleXRStarted);
          if (mode === AppMode.TWIN) route("/ground");
        };
        emitter.on("xr-started", handleXRStarted);
      }
    }
  };

  const positionContent = () => {
    setAppState(AppState.AR_PLACING);
    AppEvents.emit("content-positioning");
  };

  const xrReady = () => {
    AppEvents.emit("xr-ready");
  };

  const markOnboardingComplete = () => {
    setHasSeenOnboarding(true);
  };

  const markTwinInstructionsComplete = () => {
    setHasSeenTwinInstructions(true);
  };

  const markInteractionInstructionsComplete = () => {
    setHasSeenInteractionInstructions(true);
  };

  const value: AppContextType = {
    mode,
    appState,
    recordingState,
    modalType,
    hasSeenOnboarding,
    hasSeenTwinInstructions,
    hasSeenInteractionInstructions,
    setMode,
    setAppState,
    setRecordingState,
    showModal,
    hideModal,
    startAR,
    positionContent,
    xrReady,
    markOnboardingComplete,
    markTwinInstructionsComplete,
    markInteractionInstructionsComplete,
    isTwinMode: mode === AppMode.TWIN,
    isGroundMode: mode === AppMode.GROUND,
    isImageMode: mode === AppMode.IMAGE,
    isVPSMode: mode === AppMode.VPS,
    isTwinViewerState: appState === AppState.TWIN_VIEWER,
    isARViewerState: appState === AppState.AR_VIEWER,
    isModalState: appState === AppState.MODAL,
    isArActive: arActive,
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};
